
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Car, CarStatus } from '../types';
import { dataService, auth } from '../services/dataService';
import { onAuthStateChanged } from 'firebase/auth';
import { ChevronLeft, Calendar, Gauge, Droplet, Hash, Check, Share2, Printer } from 'lucide-react';
import { TestDriveModal } from '../components/TestDriveModal';

export const CarDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isAdminOrOwner, setIsAdminOrOwner] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      if (!id) return;
      const data = await dataService.getCarById(id);
      setCar(data || null);
      setLoading(false);
    };
    fetchCar();
  }, [id]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const staffRecord = user.email ? await dataService.getStaffMemberByEmail(user.email) : undefined;
          if (staffRecord || user.email === 'nassoroa@gmail.com' || user.email?.includes('admin')) {
            setIsAdminOrOwner(true);
          } else {
            setIsAdminOrOwner(false);
          }
        } catch (err) {
          console.error("Failed to lookup staff role in details page:", err);
          setIsAdminOrOwner(false);
        }
      } else {
        setIsAdminOrOwner(false);
      }
    });
    return () => unsubscribe();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-brandDark flex flex-col items-center justify-center text-white gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="font-heading font-bold uppercase tracking-widest">Loading Vehicle Profile...</p>
    </div>
  );

  if (!car) return (
    <div className="min-h-screen bg-brandDark flex flex-col items-center justify-center text-white">
      <h2 className="text-2xl font-heading font-bold uppercase mb-4">Vehicle Not Found</h2>
      <Link to="/inventory" className="bg-primary px-8 py-3 font-bold uppercase tracking-widest text-sm">Return to Inventory</Link>
    </div>
  );

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  return (
    <div className="bg-brandDark min-h-screen py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Link to="/inventory" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors font-bold uppercase text-xs tracking-widest">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Inventory
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Left Column - Media Gallery */}
          <div className="space-y-6">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-card border border-gray-800 shadow-2xl relative">
              <img 
                src={car.images[activeImageIndex]} 
                alt={`${car.make} ${car.model}`} 
                className="w-full h-full object-contain transition-opacity duration-300" 
              />
              <div className="absolute top-4 right-4 bg-brandDark/80 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-widest border border-white/10">
                Unit {activeImageIndex + 1} / {car.images.length}
              </div>
            </div>
            
            {car.images.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                {car.images.map((img, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => setActiveImageIndex(idx)}
                    className={`aspect-square rounded-xl overflow-hidden border-2 bg-card transition-all shadow-lg ${idx === activeImageIndex ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100 hover:border-gray-500'}`}
                  >
                    <img src={img} alt={`Gallery view ${idx}`} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Tech Specs & Acquisition */}
          <div className="flex flex-col">
            <div className="mb-4">
              {car.status === CarStatus.SOLD && <span className="bg-danger text-white text-[11px] px-3 py-1 rounded-full font-bold uppercase tracking-[0.2em] shadow-lg shadow-danger/20">Sold Out</span>}
              {car.status === CarStatus.PENDING && <span className="bg-accent text-brandDark text-[11px] px-3 py-1 rounded-full font-bold uppercase tracking-[0.2em] shadow-lg shadow-accent/20">Reservation Pending</span>}
              {car.status === CarStatus.AVAILABLE && <span className="bg-primary text-white text-[11px] px-3 py-1 rounded-full font-bold uppercase tracking-[0.2em] shadow-lg shadow-primary/20">Available for Dispatch</span>}
            </div>

            <div className="flex items-center gap-3 text-primary mb-2">
              <span className="text-sm font-bold uppercase tracking-[0.4em]">{car.year} PRODUCTION</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6 uppercase tracking-tight leading-none">
              {car.make} <br />
              <span className="text-primary">{car.model}</span>
            </h1>
            
            <p className="text-lg text-gray-400 mb-8 leading-relaxed font-light">
              {car.description}
            </p>
            
            <div className="flex items-center gap-6 mb-10 p-6 bg-card rounded-2xl border border-gray-800 shadow-inner">
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-[0.2em] mb-1">Dakar Valuation</span>
                <span className="text-5xl font-heading font-bold text-white leading-none">{formatPrice(car.price)}</span>
              </div>
              <div className="h-10 w-px bg-gray-800"></div>
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-[0.2em] mb-1">Market Avg</span>
                <span className="text-xl text-gray-500 line-through decoration-danger decoration-2 font-bold">{formatPrice(car.price * 1.05)}</span>
              </div>
            </div>

            {/* Quick Specs Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              <div className="bg-card p-4 rounded-xl border border-gray-800 text-center hover:bg-brandDark/50 transition-colors">
                <Calendar className="h-5 w-5 text-primary mx-auto mb-2" />
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">Model Year</p>
                <p className="font-bold text-white text-sm">{car.year}</p>
              </div>
              <div className="bg-card p-4 rounded-xl border border-gray-800 text-center hover:bg-brandDark/50 transition-colors">
                <Gauge className="h-5 w-5 text-accent mx-auto mb-2" />
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">Mileage</p>
                <p className="font-bold text-white text-sm">{car.mileage.toLocaleString()}</p>
              </div>
              <div className="bg-card p-4 rounded-xl border border-gray-800 text-center hover:bg-brandDark/50 transition-colors">
                <Droplet className="h-5 w-5 text-danger mx-auto mb-2" />
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">Body Color</p>
                <p className="font-bold text-white text-sm">{car.color}</p>
              </div>
              <div className="bg-card p-4 rounded-xl border border-gray-800 text-center hover:bg-brandDark/50 transition-colors">
                <Hash className="h-5 w-5 text-white/40 mx-auto mb-2" />
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">VIN Identification</p>
                {isAdminOrOwner ? (
                  <p className="font-mono font-bold text-white text-[10px] tracking-tighter uppercase truncate" title="Admin/Owner View Enabled">{car.vin}</p>
                ) : (
                  <p className="font-mono font-bold text-white/40 text-[10px] tracking-widest uppercase truncate" title="Hidden to public - Admin only">•••••••••••••••••</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button 
                onClick={() => setIsModalOpen(true)}
                disabled={car.status === CarStatus.SOLD}
                className="flex-1 bg-primary text-white py-5 px-8 rounded-xl font-heading text-xl font-bold uppercase tracking-widest hover:bg-white hover:text-primary transition-all duration-300 disabled:opacity-20 shadow-2xl shadow-primary/20 active:scale-[0.98]"
              >
                Book Inspection
              </button>
              <button className="bg-transparent border-2 border-gray-700 text-white p-5 rounded-xl hover:bg-white hover:text-brandDark hover:border-white transition-all duration-300">
                <Share2 className="h-6 w-6" />
              </button>
            </div>

            {/* Advanced Features */}
            <div className="mt-auto">
              <h3 className="font-heading text-xl font-bold text-white mb-6 uppercase tracking-widest flex items-center gap-3">
                <span className="w-8 h-1 bg-primary"></span> Technical Features
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                {car.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 text-gray-400 group">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(0,133,63,1)]"></div>
                    <span className="text-sm font-medium group-hover:text-white transition-colors">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <TestDriveModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        carId={car.id} 
        carName={`${car.year} ${car.make} ${car.model}`} 
      />
    </div>
  );
};
